/**
 * Community Export Schema Definition
 *
 * Defines the canonical JSON schema for exporting a complete energy community
 * configuration. Field names are snake_case throughout and aligned with the
 * MILP optimization engine inputs documented in ecoempower_platform_redesign.md.
 *
 * Schema hierarchy:
 *   community
 *     -> community_assets[]   (solar_pv_farm | wind_turbine | community_battery)
 *     -> buildings[]
 *         -> assets            (rooftop_pv | battery | heat_pump | hvac | ev_charger)
 *         -> datasets          (load_profile | pv_generation | energy_bill)
 *     -> datasets              (pv_generation_profile | wind_generation_profile | energy_tariff | feed_in_tariff)
 *
 * Version: 1.0
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const SCHEMA_VERSION = '1.0';

export const COMMUNITY_STATUSES = ['draft', 'active', 'archived'];

export const BUILDING_TYPES = ['residential', 'commercial', 'industrial', 'municipal'];

export const ENERGY_CLASSES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

export const COMMUNITY_ASSET_TYPES = ['solar_pv_farm', 'ground_mounted_pv', 'wind_turbine', 'community_battery'];

/**
 * All asset_type values that represent a community-level PV installation.
 * Sample datasets use 'ground_mounted_pv'; the Init Wizard creates 'solar_pv_farm'.
 * Always use this set (or the helper below) instead of hard-coding a single string.
 */
export const COMMUNITY_PV_TYPES = ['solar_pv_farm', 'ground_mounted_pv', 'pv'];

/**
 * Check whether an asset represents a community-level PV farm.
 * @param {object} asset - Asset object with asset_type field
 * @returns {boolean}
 */
export function isCommunityPV(asset) {
  return COMMUNITY_PV_TYPES.includes(asset?.asset_type);
}

export const BUILDING_ASSET_KEYS = ['rooftop_pv', 'battery', 'heat_pump', 'hvac', 'ev_charger'];

export const SUPPORTED_COUNTRIES = ['Italy', 'France', 'Germany', 'Czech Republic', 'Greece'];

export const SUPPORTED_CURRENCIES = ['EUR'];

// ---------------------------------------------------------------------------
// Field Definitions (metadata about each field for UI / validation)
// ---------------------------------------------------------------------------

/**
 * Rooftop PV fields -- aligned with optimizer `pv_available[building,time]`
 * capacity_kwp drives the PV generation upper bound per building.
 */
export const ROOFTOP_PV_FIELDS = {
  capacity_kwp:  { type: 'number', unit: 'kWp', required: true,  label: 'Installed capacity' },
  tilt_angle:    { type: 'number', unit: 'deg', required: false, label: 'Panel tilt angle', default: 30 },
  azimuth:       { type: 'number', unit: 'deg', required: false, label: 'Panel azimuth', default: 180 },
  efficiency:    { type: 'number', unit: '%',   required: false, label: 'Efficiency', default: 85 },
};

/**
 * Battery fields -- aligned with optimizer decision variables:
 *   soc[building,time], charge[building,time], discharge[building,time]
 * Constraints: soc_min <= soc[t] <= capacity_kwh
 *              charge[t] <= p_charge_kw
 *              discharge[t] <= p_discharge_kw
 */
export const BATTERY_FIELDS = {
  capacity_kwh:    { type: 'number', unit: 'kWh', required: true,  label: 'Capacity' },
  p_charge_kw:     { type: 'number', unit: 'kW',  required: true,  label: 'Max charge power' },
  p_discharge_kw:  { type: 'number', unit: 'kW',  required: true,  label: 'Max discharge power' },
  round_trip_eff:  { type: 'number', unit: '%',    required: false, label: 'Round-trip efficiency', default: 90 },
  initial_soc:     { type: 'number', unit: '%',    required: false, label: 'Initial state of charge', default: 50 },
};

/**
 * Heat pump fields -- aligned with optimizer thermal model (1R1C):
 *   u_heat[building,time] bounded by rated_power_kw / cop
 */
export const HEAT_PUMP_FIELDS = {
  rated_power_kw: { type: 'number', unit: 'kW',  required: true,  label: 'Rated power' },
  cop:            { type: 'number', unit: null,   required: false, label: 'COP (Coefficient of Performance)', default: 3.0 },
  controllable:   { type: 'select', options: ['yes', 'no'], required: false, label: 'Controllable', default: 'yes' },
};

/**
 * HVAC fields -- aligned with optimizer u_cool[building,time]
 */
export const HVAC_FIELDS = {
  rated_power_kw: { type: 'number', unit: 'kW', required: true,  label: 'Rated power' },
  controllable:   { type: 'select', options: ['yes', 'no'], required: false, label: 'Controllable', default: 'yes' },
};

/**
 * EV charger fields -- aligned with optimizer ev[building,time]:
 *   ev[t] <= max_power_kw * num_evs
 *   sum(ev[t]) = daily_energy_per_ev_kwh * num_evs (within window)
 */
export const EV_CHARGER_FIELDS = {
  max_power_kw:           { type: 'number', unit: 'kW',  required: true,  label: 'Max charging power', default: 7.4 },
  num_evs:                { type: 'number', unit: null,   required: true,  label: 'Number of EVs' },
  daily_energy_per_ev_kwh:{ type: 'number', unit: 'kWh', required: true,  label: 'Daily energy demand per EV' },
  controllable:           { type: 'select', options: ['yes', 'no'], required: false, label: 'Smart charging', default: 'yes' },
};

/**
 * Community-level Solar PV Farm fields
 */
export const SOLAR_PV_FARM_FIELDS = {
  capacity_kwp:       { type: 'number', unit: 'kWp', required: true,  label: 'Capacity' },
  efficiency:         { type: 'number', unit: '%',   required: true,  label: 'Efficiency', default: 18 },
  tilt_angle:         { type: 'number', unit: 'deg', required: false, label: 'Tilt angle', default: 35 },
  azimuth:            { type: 'number', unit: 'deg', required: false, label: 'Azimuth', default: 180 },
  installation_date:  { type: 'date',   unit: null,  required: false, label: 'Installation date' },
};

/**
 * Community-level Wind Turbine fields
 */
export const WIND_TURBINE_FIELDS = {
  capacity_kw:       { type: 'number', unit: 'kW',  required: true,  label: 'Capacity' },
  hub_height:        { type: 'number', unit: 'm',   required: false, label: 'Hub height', default: 50 },
  rotor_diameter:    { type: 'number', unit: 'm',   required: false, label: 'Rotor diameter' },
  cut_in_speed:      { type: 'number', unit: 'm/s', required: false, label: 'Cut-in speed', default: 3 },
  installation_date: { type: 'date',   unit: null,  required: false, label: 'Installation date' },
};

/**
 * Community-level Battery Storage fields -- aligned with optimizer:
 *   cb_soc[time], cb_charge[time], cb_discharge[time]
 */
export const COMMUNITY_BATTERY_FIELDS = {
  capacity_kwh:          { type: 'number', unit: 'kWh', required: true,  label: 'Capacity' },
  p_charge_kw:           { type: 'number', unit: 'kW',  required: true,  label: 'Max charge power' },
  p_discharge_kw:        { type: 'number', unit: 'kW',  required: true,  label: 'Max discharge power' },
  efficiency_charge:     { type: 'number', unit: '%',   required: false, label: 'Charge efficiency', default: 95 },
  efficiency_discharge:  { type: 'number', unit: '%',   required: false, label: 'Discharge efficiency', default: 95 },
  soc_min:               { type: 'number', unit: '%',   required: false, label: 'Min state of charge', default: 10 },
  soc_max:               { type: 'number', unit: '%',   required: false, label: 'Max state of charge', default: 90 },
};

/**
 * Map of community asset type -> its field definitions
 */
export const COMMUNITY_ASSET_FIELD_MAP = {
  solar_pv_farm: SOLAR_PV_FARM_FIELDS,
  ground_mounted_pv: SOLAR_PV_FARM_FIELDS,
  wind_turbine: WIND_TURBINE_FIELDS,
  community_battery: COMMUNITY_BATTERY_FIELDS,
};

/**
 * Map of building asset key -> its field definitions
 */
export const BUILDING_ASSET_FIELD_MAP = {
  rooftop_pv: ROOFTOP_PV_FIELDS,
  battery: BATTERY_FIELDS,
  heat_pump: HEAT_PUMP_FIELDS,
  hvac: HVAC_FIELDS,
  ev_charger: EV_CHARGER_FIELDS,
};


// ---------------------------------------------------------------------------
// Default Object Generators
// ---------------------------------------------------------------------------

/**
 * Generate a UUID-like identifier (for client-side use).
 * In production, the backend assigns real UUIDs.
 */
const generateId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Generate default spec object for a given field definition map,
 * populating only fields that have a defined default.
 */
const generateDefaultSpec = (fieldMap) => {
  const spec = {};
  Object.entries(fieldMap).forEach(([key, def]) => {
    if (def.default !== undefined) {
      spec[key] = def.default;
    }
  });
  return spec;
};

/**
 * Build a dataset reference stub (null by default).
 */
const emptyDatasetRef = () => null;

/**
 * Generate a valid empty/default community object conforming to the schema.
 * @param {Object} overrides - Optional partial community data to merge
 * @returns {Object} A complete community export object with defaults
 */
export const createDefaultCommunity = (overrides = {}) => {
  const now = new Date().toISOString();
  return {
    schema_version: SCHEMA_VERSION,
    exported_at: now,
    community: {
      id: generateId(),
      name: '',
      country: '',
      location: '',
      timezone: '',
      currency: 'EUR',
      description: '',
      status: 'draft',
      created_at: now,
      updated_at: now,

      community_assets: [],

      buildings: [],

      datasets: {
        pv_generation_profile: emptyDatasetRef(),
        wind_generation_profile: emptyDatasetRef(),
        energy_tariff: emptyDatasetRef(),
        feed_in_tariff: emptyDatasetRef(),
      },

      ...overrides,
    },
  };
};

/**
 * Generate a default building object.
 * @param {Object} overrides - Optional partial building data
 * @returns {Object} Building conforming to schema
 */
export const createDefaultBuilding = (overrides = {}) => {
  const now = new Date().toISOString();
  return {
    id: generateId(),
    building_id: '',
    building_type: 'residential',
    floor_area_m2: null,
    energy_class: 'C',
    created_at: now,

    assets: {
      rooftop_pv: null,
      battery: null,
      heat_pump: null,
      hvac: null,
      ev_charger: null,
    },

    datasets: {
      load_profile: null,
      pv_generation: null,
      energy_bill: null,
    },

    ...overrides,
  };
};

/**
 * Generate a default community asset object for a given type.
 * @param {string} assetType - One of COMMUNITY_ASSET_TYPES
 * @param {Object} overrides - Optional partial data
 * @returns {Object} Community asset conforming to schema
 */
export const createDefaultCommunityAsset = (assetType, overrides = {}) => {
  const fieldMap = COMMUNITY_ASSET_FIELD_MAP[assetType];
  if (!fieldMap) {
    throw new Error(`Unknown community asset type: ${assetType}`);
  }

  return {
    id: generateId(),
    type: assetType,
    name: '',
    owner_scope: 'community',
    status: 'active',
    specs: generateDefaultSpec(fieldMap),
    created_at: new Date().toISOString(),
    ...overrides,
  };
};


// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validate a community export object against the schema.
 * Returns an object { valid: boolean, errors: string[] }.
 *
 * @param {Object} data - The community export object to validate
 * @returns {{ valid: boolean, errors: string[] }}
 */
export const validateCommunityExport = (data) => {
  const errors = [];

  // Top-level checks
  if (!data) {
    return { valid: false, errors: ['Data is null or undefined'] };
  }

  if (!data.schema_version) {
    errors.push('Missing schema_version');
  }

  if (!data.community) {
    errors.push('Missing community object');
    return { valid: false, errors };
  }

  const c = data.community;

  // --- Community Info ---
  if (!c.name || typeof c.name !== 'string' || c.name.trim() === '') {
    errors.push('Community name is required');
  }

  if (!c.country || typeof c.country !== 'string' || c.country.trim() === '') {
    errors.push('Community country is required');
  }

  if (!c.timezone || typeof c.timezone !== 'string') {
    errors.push('Community timezone is required');
  }

  if (c.currency && !SUPPORTED_CURRENCIES.includes(c.currency)) {
    errors.push(`Unsupported currency: ${c.currency}. Supported: ${SUPPORTED_CURRENCIES.join(', ')}`);
  }

  if (c.status && !COMMUNITY_STATUSES.includes(c.status)) {
    errors.push(`Invalid community status: ${c.status}. Allowed: ${COMMUNITY_STATUSES.join(', ')}`);
  }

  // --- Community Assets ---
  if (c.community_assets && Array.isArray(c.community_assets)) {
    c.community_assets.forEach((asset, idx) => {
      const prefix = `community_assets[${idx}]`;

      if (!asset.type || !COMMUNITY_ASSET_TYPES.includes(asset.type)) {
        errors.push(`${prefix}: Invalid or missing asset type '${asset.type}'`);
      }

      if (!asset.name || typeof asset.name !== 'string' || asset.name.trim() === '') {
        errors.push(`${prefix}: Asset name is required`);
      }

      // Validate required spec fields
      const fieldMap = COMMUNITY_ASSET_FIELD_MAP[asset.type];
      if (fieldMap && asset.specs) {
        Object.entries(fieldMap).forEach(([fieldName, fieldDef]) => {
          if (fieldDef.required) {
            const val = asset.specs[fieldName];
            if (val === null || val === undefined || val === '') {
              errors.push(`${prefix}.specs.${fieldName}: ${fieldDef.label} is required`);
            }
          }
        });
      }
    });
  }

  // --- Buildings ---
  if (c.buildings && Array.isArray(c.buildings)) {
    const buildingIds = [];

    c.buildings.forEach((bldg, idx) => {
      const prefix = `buildings[${idx}]`;

      if (!bldg.building_id || typeof bldg.building_id !== 'string' || bldg.building_id.trim() === '') {
        errors.push(`${prefix}: building_id is required`);
      } else {
        if (buildingIds.includes(bldg.building_id)) {
          errors.push(`${prefix}: Duplicate building_id '${bldg.building_id}'`);
        }
        buildingIds.push(bldg.building_id);
      }

      if (!bldg.building_type || !BUILDING_TYPES.includes(bldg.building_type)) {
        errors.push(`${prefix}: Invalid or missing building_type '${bldg.building_type}'`);
      }

      if (!bldg.floor_area_m2 || typeof bldg.floor_area_m2 !== 'number' || bldg.floor_area_m2 <= 0) {
        errors.push(`${prefix}: floor_area_m2 must be a positive number`);
      }

      if (bldg.energy_class && !ENERGY_CLASSES.includes(bldg.energy_class)) {
        errors.push(`${prefix}: Invalid energy_class '${bldg.energy_class}'`);
      }

      // Validate building assets
      if (bldg.assets) {
        BUILDING_ASSET_KEYS.forEach((assetKey) => {
          const assetData = bldg.assets[assetKey];
          if (assetData !== null && assetData !== undefined) {
            const fieldMap = BUILDING_ASSET_FIELD_MAP[assetKey];
            if (fieldMap) {
              Object.entries(fieldMap).forEach(([fieldName, fieldDef]) => {
                if (fieldDef.required) {
                  const val = assetData[fieldName];
                  if (val === null || val === undefined || val === '') {
                    errors.push(
                      `${prefix}.assets.${assetKey}.${fieldName}: ${fieldDef.label} is required`
                    );
                  }
                }
              });
            }
          }
        });
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};


// ---------------------------------------------------------------------------
// Form State Conversion
// ---------------------------------------------------------------------------

/**
 * Convert a building asset from the form format (flat fields) to the schema
 * format (structured under assets{}).
 *
 * Form format stores: has_rooftop_pv, rooftop_pv: { capacity_kwp, ... }
 * Schema format stores: assets.rooftop_pv: { capacity_kwp, ... } | null
 *
 * @param {Object} formBuilding - Building data as stored in BuildingForm state
 * @param {Object} [buildingDatasets] - Optional map of dataset type -> dataset reference
 *   e.g. { load_profile: { dataset_id, name, ... }, pv_generation: null, energy_bill: null }
 * @returns {Object} Building in schema format
 */
export const convertBuildingFromForm = (formBuilding, buildingDatasets = null) => {
  const assets = {};

  BUILDING_ASSET_KEYS.forEach((key) => {
    const hasAsset = formBuilding[`has_${key}`];
    const assetData = formBuilding[key];

    if (hasAsset && assetData && typeof assetData === 'object' && Object.keys(assetData).length > 0) {
      // Apply defaults for missing optional fields
      const fieldMap = BUILDING_ASSET_FIELD_MAP[key];
      const enriched = { ...assetData };
      if (fieldMap) {
        Object.entries(fieldMap).forEach(([fieldName, fieldDef]) => {
          if (enriched[fieldName] === undefined && fieldDef.default !== undefined) {
            enriched[fieldName] = fieldDef.default;
          }
        });
      }
      assets[key] = enriched;
    } else {
      assets[key] = null;
    }
  });

  return {
    id: formBuilding.id || generateId(),
    building_id: formBuilding.building_id || '',
    building_type: formBuilding.building_type || 'residential',
    floor_area_m2: formBuilding.floor_area ? Number(formBuilding.floor_area) : null,
    energy_class: formBuilding.energy_class || null,
    created_at: formBuilding.createdAt || new Date().toISOString(),
    assets,
    datasets: buildingDatasets || {
      load_profile: null,
      pv_generation: null,
      energy_bill: null,
    },
  };
};

/**
 * Convert a community asset from the form format (AssetForm state) to the
 * schema format.
 *
 * Form format: { name, ownerScope, type, specs: { ... }, ... }
 * Schema format: { id, type, name, owner_scope, status, specs: { ... }, created_at }
 *
 * @param {Object} formAsset - Asset data as stored in AssetForm state
 * @returns {Object} Community asset in schema format
 */
export const convertCommunityAssetFromForm = (formAsset) => {
  const specs = { ...(formAsset.specs || {}) };

  // Apply defaults for missing optional fields
  const fieldMap = COMMUNITY_ASSET_FIELD_MAP[formAsset.type];
  if (fieldMap) {
    Object.entries(fieldMap).forEach(([fieldName, fieldDef]) => {
      if (specs[fieldName] === undefined && fieldDef.default !== undefined) {
        specs[fieldName] = fieldDef.default;
      }
    });
  }

  return {
    id: formAsset.id || generateId(),
    type: formAsset.type,
    name: formAsset.name || '',
    owner_scope: formAsset.ownerScope || 'community',
    status: formAsset.status || 'active',
    specs,
    created_at: formAsset.createdAt || new Date().toISOString(),
  };
};

/**
 * Convert the full community form state (spread across CommunitySetupPage,
 * AssetsSetupPage, and BuildingsSetupPage) into the canonical export schema.
 *
 * @param {Object} params
 * @param {Object} params.communityInfo - From CommunitySetupPage form state
 * @param {Array}  params.communityAssets - From AssetsSetupPage state
 * @param {Array}  params.buildings - From BuildingsSetupPage state
 * @param {Object} [params.communityDatasets] - Optional community-level dataset refs
 * @param {Object} [params.buildingDatasetsMap] - Optional map of buildingId -> datasets
 *   e.g. { "bldg-001": { load_profile: {...}, pv_generation: null, energy_bill: null }, ... }
 * @param {string} [params.communityId] - Existing community ID (if editing)
 * @returns {Object} Complete community export object
 */
export const convertFormStateToCommunityExport = ({
  communityInfo = {},
  communityAssets = [],
  buildings = [],
  communityDatasets = {},
  buildingDatasetsMap = {},
  communityId = null,
}) => {
  const now = new Date().toISOString();

  return {
    schema_version: SCHEMA_VERSION,
    exported_at: now,
    community: {
      id: communityId || communityInfo.id || generateId(),
      name: communityInfo.name || '',
      country: communityInfo.country || '',
      location: communityInfo.location || '',
      timezone: communityInfo.timezone || '',
      currency: communityInfo.currency || 'EUR',
      description: communityInfo.description || '',
      status: communityInfo.status || 'draft',
      created_at: communityInfo.createdAt || now,
      updated_at: now,

      community_assets: communityAssets.map(convertCommunityAssetFromForm),

      buildings: buildings.map((bldg) => {
        const bldgDatasets = buildingDatasetsMap[bldg.id] || null;
        return convertBuildingFromForm(bldg, bldgDatasets);
      }),

      datasets: {
        pv_generation_profile: communityDatasets.pv_generation_profile || null,
        wind_generation_profile: communityDatasets.wind_generation_profile || null,
        energy_tariff: communityDatasets.energy_tariff || null,
        feed_in_tariff: communityDatasets.feed_in_tariff || null,
      },
    },
  };
};


// ---------------------------------------------------------------------------
// Dataset Reference Helper
// ---------------------------------------------------------------------------

/**
 * Create a dataset reference object from upload metadata.
 *
 * This reference is stored in the exported JSON and contains only metadata
 * about the uploaded dataset -- the actual CSV data rows are NOT embedded
 * (they would be too large). The reference includes enough information
 * for the backend to locate and validate the dataset.
 *
 * @param {Object} params
 * @param {string} params.dataset_id - UUID of the dataset in the backend
 * @param {string} params.name - Human-readable name
 * @param {string} params.filename - Original filename
 * @param {string} params.type - Dataset type (e.g. 'building_load_profile')
 * @param {number} params.record_count - Number of data points
 * @param {number} params.coverage_pct - Coverage percentage
 * @param {Object} [params.date_range] - { start, end } ISO strings
 * @param {string} [params.time_resolution] - 'hourly', 'daily', 'monthly', etc.
 * @param {string} params.uploaded_at - ISO timestamp
 * @param {Object} [params.validation] - { valid, errors, warnings } counts
 * @returns {Object} Dataset reference for the schema
 */
export const createDatasetReference = ({
  dataset_id = null,
  name = '',
  filename = '',
  type = '',
  record_count = 0,
  coverage_pct = 0,
  date_range = null,
  time_resolution = null,
  uploaded_at = null,
  validation = null,
} = {}) => ({
  dataset_id: dataset_id || generateId(),
  name,
  filename,
  type,
  record_count,
  coverage_pct,
  date_range: date_range || { start: null, end: null },
  time_resolution: time_resolution || null,
  uploaded_at: uploaded_at || new Date().toISOString(),
  validation: validation || { valid: true, errors: 0, warnings: 0 },
});


/**
 * Build a dataset reference from a validation report produced by csvValidator.
 *
 * Convenience function that maps the validation report fields to the
 * createDatasetReference format.
 *
 * @param {Object} params
 * @param {string} params.filename   - Original CSV filename
 * @param {string} params.name       - Human-readable name for the dataset
 * @param {Object} params.report     - Validation report from validateCSVAgainstSchema
 * @returns {Object} Dataset reference for the schema
 */
export const createDatasetReferenceFromReport = ({
  filename = '',
  name = '',
  report = {},
} = {}) => {
  const summary = report.summary || {};
  return createDatasetReference({
    name: name || filename.replace(/\.csv$/i, ''),
    filename,
    type: report.datasetType || '',
    record_count: summary.totalRows || 0,
    coverage_pct: summary.totalRows > 0
      ? Math.round((summary.validRows / summary.totalRows) * 100)
      : 0,
    date_range: summary.dateRange || null,
    time_resolution: summary.timeResolution || null,
    uploaded_at: new Date().toISOString(),
    validation: {
      valid: report.valid || false,
      errors: (report.errors || []).length,
      warnings: (report.warnings || []).length,
    },
  });
};
